/**
 * @fileoverview Centralized event management for Table
 *
 * Manages all event listeners with automatic cleanup to prevent memory leaks.
 * Provides a registry of all active event handlers for debugging.
 *
 * @module EventManager
 */

/**
 * @typedef {import('./types.js').EventHandler} EventHandler
 */

/**
 * EventManager - Centralized event handler tracking and cleanup
 * @class
 */
export class EventManager {
  /**
   * Create an EventManager instance
   */
  constructor() {
    this.handlers = [];
    this.handlerIdCounter = 0;
  }

  /**
   * Add an event listener and track it
   * @param {HTMLElement|Window|Document} element - Element to attach event to
   * @param {string} event - Event name (e.g., 'click', 'change')
   * @param {function} handler - Event handler function
   * @param {Object} [options] - Event listener options
   * @returns {number} Handler ID for later removal
   */
  on(element, event, handler, options = {}) {
    if (!element || typeof element.addEventListener !== 'function') {
      console.warn('EventManager: Invalid element for event listener');
      return -1;
    }

    if (typeof handler !== 'function') {
      console.warn('EventManager: Handler must be a function');
      return -1;
    }

    const handlerId = ++this.handlerIdCounter;

    // Add event listener
    element.addEventListener(event, handler, options);

    // Track for cleanup
    this.handlers.push({
      id: handlerId,
      element,
      event,
      handler,
      options,
      timestamp: Date.now()
    });

    return handlerId;
  }

  /**
   * Remove a specific event listener by ID
   * @param {number} handlerId - Handler ID returned from on()
   * @returns {boolean} True if handler was found and removed
   */
  off(handlerId) {
    const index = this.handlers.findIndex(h => h.id === handlerId);

    if (index === -1) {
      return false;
    }

    const handler = this.handlers[index];

    try {
      handler.element.removeEventListener(handler.event, handler.handler, handler.options);
      this.handlers.splice(index, 1);
      return true;
    } catch (error) {
      console.warn('EventManager: Error removing event listener:', error);
      return false;
    }
  }

  /**
   * Remove all event listeners for a specific element
   * @param {HTMLElement} element - Element to remove listeners from
   * @returns {number} Number of handlers removed
   */
  offElement(element) {
    const toRemove = this.handlers.filter(h => h.element === element);

    toRemove.forEach(handler => {
      try {
        handler.element.removeEventListener(handler.event, handler.handler, handler.options);
      } catch (error) {
        console.warn('EventManager: Error removing event listener:', error);
      }
    });

    this.handlers = this.handlers.filter(h => h.element !== element);

    return toRemove.length;
  }

  /**
   * Remove all event listeners for a specific event type
   * @param {string} event - Event name
   * @returns {number} Number of handlers removed
   */
  offEvent(event) {
    const toRemove = this.handlers.filter(h => h.event === event);

    toRemove.forEach(handler => {
      try {
        handler.element.removeEventListener(handler.event, handler.handler, handler.options);
      } catch (error) {
        console.warn('EventManager: Error removing event listener:', error);
      }
    });

    this.handlers = this.handlers.filter(h => h.event !== event);

    return toRemove.length;
  }

  /**
   * Remove all tracked event listeners
   * @returns {number} Number of handlers removed
   */
  offAll() {
    const count = this.handlers.length;

    this.handlers.forEach(handler => {
      try {
        handler.element.removeEventListener(handler.event, handler.handler, handler.options);
      } catch (error) {
        console.warn('EventManager: Error removing event listener:', error);
      }
    });

    this.handlers = [];

    return count;
  }

  /**
   * Emit a custom event on an element
   * @param {HTMLElement} element - Element to emit event on
   * @param {string} eventName - Event name
   * @param {*} [detail] - Event detail data
   * @param {Object} [options] - Event options
   * @returns {boolean} False if event was cancelled
   */
  emit(element, eventName, detail = null, options = {}) {
    if (!element || typeof element.dispatchEvent !== 'function') {
      console.warn('EventManager: Invalid element for event dispatch');
      return false;
    }

    const event = new CustomEvent(eventName, {
      detail,
      bubbles: options.bubbles !== false,
      cancelable: options.cancelable !== false,
      composed: options.composed || false
    });

    return element.dispatchEvent(event);
  }

  /**
   * Add a one-time event listener
   * @param {HTMLElement} element - Element to attach event to
   * @param {string} event - Event name
   * @param {function} handler - Event handler function
   * @returns {number} Handler ID
   */
  once(element, event, handler) {
    const wrappedHandler = (e) => {
      handler(e);
      this.off(handlerId);
    };

    const handlerId = this.on(element, event, wrappedHandler);
    return handlerId;
  }

  /**
   * Add a delegated event listener (event delegation)
   * @param {HTMLElement} element - Parent element
   * @param {string} event - Event name
   * @param {string} selector - Child selector
   * @param {function} handler - Event handler function
   * @returns {number} Handler ID
   */
  delegate(element, event, selector, handler) {
    const delegatedHandler = (e) => {
      const target = e.target.closest(selector);
      if (target && element.contains(target)) {
        handler.call(target, e);
      }
    };

    return this.on(element, event, delegatedHandler);
  }

  /**
   * Get all registered handlers (for debugging)
   * @returns {Array<EventHandler>} Array of handler info
   */
  getHandlers() {
    return this.handlers.map(h => ({
      id: h.id,
      event: h.event,
      element: h.element.tagName || 'Unknown',
      timestamp: h.timestamp
    }));
  }

  /**
   * Get number of registered handlers
   * @returns {number} Handler count
   */
  getHandlerCount() {
    return this.handlers.length;
  }

  /**
   * Get handlers for a specific element
   * @param {HTMLElement} element - Element to check
   * @returns {Array} Handler info
   */
  getHandlersForElement(element) {
    return this.handlers
      .filter(h => h.element === element)
      .map(h => ({
        id: h.id,
        event: h.event,
        timestamp: h.timestamp
      }));
  }

  /**
   * Get handlers for a specific event type
   * @param {string} event - Event name
   * @returns {Array} Handler info
   */
  getHandlersForEvent(event) {
    return this.handlers
      .filter(h => h.event === event)
      .map(h => ({
        id: h.id,
        element: h.element.tagName || 'Unknown',
        timestamp: h.timestamp
      }));
  }

  /**
   * Check if there are any handlers for an element
   * @param {HTMLElement} element - Element to check
   * @returns {boolean} True if element has handlers
   */
  hasHandlers(element) {
    return this.handlers.some(h => h.element === element);
  }

  /**
   * Throttle an event handler
   * @param {function} handler - Handler function
   * @param {number} delay - Throttle delay in ms
   * @returns {function} Throttled handler
   */
  static throttle(handler, delay) {
    let lastCall = 0;
    let timeout = null;

    return function throttled(...args) {
      const now = Date.now();
      const timeSinceLastCall = now - lastCall;

      if (timeSinceLastCall >= delay) {
        lastCall = now;
        handler.apply(this, args);
      } else {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          lastCall = Date.now();
          handler.apply(this, args);
        }, delay - timeSinceLastCall);
      }
    };
  }

  /**
   * Debounce an event handler
   * @param {function} handler - Handler function
   * @param {number} delay - Debounce delay in ms
   * @returns {function} Debounced handler
   */
  static debounce(handler, delay) {
    let timeout = null;

    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        handler.apply(this, args);
      }, delay);
    };
  }
}

export default EventManager;
